import { useState } from "react";
import { motion } from "framer-motion";
import { Feather, ChevronDown, ChevronUp } from "lucide-react";
import { LQReflection } from "@/hooks/useLaylatulQadr";

interface ReflectionJournalProps {
  nightNumber: number;
  reflections: LQReflection[];
  onSave: (nightNumber: number, text: string, prompt?: string) => Promise<void>;
}

const PROMPTS = [
  "What moment tonight felt most sincere?",
  "Which dua did you return to the most?",
  "What is on your heart?",
  "What are you most grateful for tonight?",
  "What felt different about your worship tonight?",
];

export const ReflectionJournal = ({ nightNumber, reflections, onSave }: ReflectionJournalProps) => {
  const currentReflection = reflections.find(r => r.night_number === nightNumber);
  const pastReflections = reflections.filter(r => r.night_number !== nightNumber).sort((a, b) => b.night_number - a.night_number);
  const [text, setText] = useState(currentReflection?.reflection || "");
  const [selectedPrompt, setSelectedPrompt] = useState(
    currentReflection?.prompt || PROMPTS[nightNumber % PROMPTS.length]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(!currentReflection?.reflection);
  const [showArchive, setShowArchive] = useState(false);

  const handleSave = async () => {
    if (!text.trim()) return;
    setIsSaving(true);
    await onSave(nightNumber, text.trim(), selectedPrompt);
    setIsSaving(false);
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4 }}
      className="space-y-4"
    >
      <h2 className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-3">
        <Feather className="w-4 h-4" />
        Reflection Journal
      </h2>

      <div className="bg-[#0f0f23]/50 rounded-sm p-6 border border-gold/10 space-y-4">
        {/* Prompt selector */}
        <div className="space-y-2">
          <p className="text-cream/60 text-xs uppercase tracking-wider">Reflect on tonight</p>
          <div className="flex flex-wrap gap-2">
            {PROMPTS.map((prompt) => (
              <button
                key={prompt}
                onClick={() => setSelectedPrompt(prompt)}
                className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${
                  selectedPrompt === prompt
                    ? "bg-gold/15 text-gold border border-gold/30"
                    : "text-cream/40 border border-cream/10 hover:border-gold/20 hover:text-cream/60"
                }`}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>

        {/* Current night reflection */}
        {isEditing ? (
          <>
            <p className="text-cream/80 text-sm italic">"{selectedPrompt}"</p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write your reflection..."
              className="w-full bg-transparent border border-gold/15 rounded-sm p-4 text-cream/90 text-sm placeholder:text-cream/30 focus:outline-none focus:border-gold/30 resize-none min-h-[120px]"
              rows={4}
            />
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={isSaving || !text.trim()}
                className="px-5 py-2 border border-gold/50 text-gold text-xs uppercase tracking-wider hover:bg-gold/10 transition-colors rounded-sm disabled:opacity-40"
              >
                {isSaving ? "Saving..." : "Save Reflection"}
              </button>
              {currentReflection?.reflection && (
                <button
                  onClick={() => { setIsEditing(false); setText(currentReflection.reflection); }}
                  className="px-5 py-2 text-cream/40 text-xs uppercase tracking-wider hover:text-cream/60 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <p className="text-cream/50 text-xs italic">"{currentReflection?.prompt}"</p>
            <p className="text-cream/85 leading-relaxed text-sm">{currentReflection?.reflection}</p>
            <button
              onClick={() => setIsEditing(true)}
              className="text-gold/60 text-xs uppercase tracking-wider hover:text-gold transition-colors"
            >
              Edit reflection
            </button>
          </div>
        )}
      </div>

      {/* Past reflections archive */}
      {pastReflections.length > 0 && (
        <div className="space-y-3">
          <button
            onClick={() => setShowArchive(!showArchive)}
            className="flex items-center gap-2 text-cream/40 text-xs uppercase tracking-wider hover:text-cream/60 transition-colors"
          >
            {showArchive ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Past reflections ({pastReflections.length})
          </button>

          {showArchive && (
            <div className="space-y-3">
              {pastReflections.map((ref) => (
                <motion.div
                  key={ref.night_number}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-gradient-to-r from-gold/5 to-transparent rounded-sm p-5 border-l-2 border-gold/30"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-gold/15 text-gold px-2.5 py-0.5 rounded-full text-xs">
                      Night {ref.night_number}
                    </span>
                  </div>
                  {ref.prompt && (
                    <p className="text-cream/40 text-xs italic mb-1">"{ref.prompt}"</p>
                  )}
                  <p className="text-cream/70 text-sm leading-relaxed">{ref.reflection}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};
